// // src/Components/OrderConfirmation/OrderConfirmation.tsx
// import React, { useEffect, useState, useRef, useCallback } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import Header from '../Header/Header';
// import Footer from '../Footer/Footer';
// import BackToTop from '../Back-to-top/BackToTop';
// import HeaderSpacer from '../Header/HeaderSpacer';
// import { CheckoutService } from '../../Services/checkoutService';
// import { useCompany } from '../../Contexts/CompanyContext';

// // ─────────────────────────────────────────────────────────────────────────────
// // Constants
// // ─────────────────────────────────────────────────────────────────────────────

// // The localStorage key used by Checkout.tsx to back up the cart before PayHere
// // redirect. We restore from here on payment failure / cancellation.
// const CART_BACKUP_KEY = 'payhere_cart_backup';

// // paymentStatus values that mean we can stop polling
// const TERMINAL_PAYMENT_STATUSES = new Set([
//   'paid',
//   'cancelled',
//   'failed',
//   'chargedback',
//   // Non-PayHere orders land here immediately
//   'pending',
// ]);

// const POLL_INTERVAL_MS  = 3000;   // poll every 3 seconds
// const POLL_TIMEOUT_MS   = 90000;  // stop after 90 seconds

// // ─────────────────────────────────────────────────────────────────────────────
// // Types
// // ─────────────────────────────────────────────────────────────────────────────

// interface OrderItem {
//   productName: string;
//   variantSelections?: Array<{ variantType: string; variantValue: string }>;
//   quantity: number;
//   unitPrice: number;
//   discountedUnitPrice: number;
//   discountPercentage: number;
//   totalPrice: number;
// }

// interface ShippingAddress {
//   fullName: string;
//   addressLine1: string;
//   addressLine2?: string;
//   city: string;
//   state: string;
//   postalCode: string;
//   country: string;
//   phoneNumber: string;
// }

// interface Order {
//   id: string;
//   orderNumber: string;
//   customerName: string;
//   customerEmail: string;
//   customerPhone: string;
//   items: OrderItem[];
//   subtotal: number;
//   taxAmount: number;
//   shippingCost: number;
//   discountAmount?: number;
//   totalAmount: number;
//   status: string;
//   paymentStatus: string;
//   paymentMethod: string;
//   shippingAddress: ShippingAddress;
//   orderDate: Date;
//   [key: string]: unknown;
// }

// // ─────────────────────────────────────────────────────────────────────────────
// // Cart-restore helper
// // Called when PayHere returns a failure/cancellation result so the customer
// // doesn't lose their items.
// // ─────────────────────────────────────────────────────────────────────────────
// const restoreCartFromBackup = (): boolean => {
//   try {
//     const raw = localStorage.getItem(CART_BACKUP_KEY);
//     if (!raw) return false;

//     const backup = JSON.parse(raw) as { cartItems: unknown[]; savedAt: number };

//     // Only restore backups younger than 2 hours
//     if (Date.now() - backup.savedAt > 2 * 60 * 60 * 1000) {
//       localStorage.removeItem(CART_BACKUP_KEY);
//       return false;
//     }

//     // Write back into the cart key that cartService.getCart() reads
//     localStorage.setItem('cart', JSON.stringify(backup.cartItems));
//     localStorage.removeItem(CART_BACKUP_KEY);
//     return true;
//   } catch {
//     return false;
//   }
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // Helpers
// // ─────────────────────────────────────────────────────────────────────────────

// const getVariantText = (
//   variantSelections?: Array<{ variantType: string; variantValue: string }>
// ): string => {
//   if (!variantSelections || variantSelections.length === 0) return '';
//   return variantSelections.map(s => `${s.variantType}: ${s.variantValue}`).join(', ');
// };

// const paymentStatusBadgeStyle = (paymentStatus: string): React.CSSProperties => {
//   switch (paymentStatus) {
//     case 'paid':
//     case 'pending': // non-PayHere orders (bank_transfer / COD)
//       return { backgroundColor: '#d4edda', color: '#155724' };
//     case 'awaiting_payment':
//       return { backgroundColor: '#fff3cd', color: '#856404' };
//     case 'cancelled':
//     case 'failed':
//     case 'chargedback':
//       return { backgroundColor: '#f8d7da', color: '#721c24' };
//     default:
//       return { backgroundColor: '#e2e3e5', color: '#383d41' };
//   }
// };

// const statusBadgeStyle = (): React.CSSProperties => ({
//   backgroundColor: '#fff3cd',
//   color: '#856404',
//   padding: '4px 12px',
//   borderRadius: '4px',
//   textTransform: 'capitalize' as const,
// });

// // ─────────────────────────────────────────────────────────────────────────────
// // Component
// // ─────────────────────────────────────────────────────────────────────────────

// const OrderConfirmation: React.FC = () => {
//   const { orderId } = useParams<{ orderId: string }>();
//   const navigate = useNavigate();
//   const { formatPrice } = useCompany();

//   const [order, setOrder] = useState<Order | null>(null);
//   const [loading, setLoading] = useState(true);
//   const [fetchError, setFetchError] = useState('');
//   const [pollingTimedOut, setPollingTimedOut] = useState(false);
//   const [cartRestored, setCartRestored] = useState(false);

//   const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
//   const pollTimeoutRef  = useRef<ReturnType<typeof setTimeout>  | null>(null);
//   const isMountedRef    = useRef(true);
//   // Prevent restoring the cart more than once
//   const cartRestoredRef = useRef(false);

//   const stopPolling = useCallback(() => {
//     if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
//     if (pollTimeoutRef.current)  clearTimeout(pollTimeoutRef.current);
//   }, []);

//   // Fetch order via Cloud Function (same as CheckoutService.getOrderById)
//   const fetchOrder = useCallback(async (): Promise<Order | null> => {
//     if (!orderId) return null;
//     try {
//       const result = await CheckoutService.getOrderById(orderId);
//       if (result.success && result.order) {
//         const raw = result.order as Record<string, unknown>;
//         // orderDate may come back as a Firestore Timestamp or ISO string
//         let orderDate: Date = new Date();
//         if (raw.orderDate) {
//           if (
//             typeof raw.orderDate === 'object' &&
//             raw.orderDate !== null &&
//             'toDate' in raw.orderDate &&
//             typeof (raw.orderDate as { toDate: () => Date }).toDate === 'function'
//           ) {
//             orderDate = (raw.orderDate as { toDate: () => Date }).toDate();
//           } else if (typeof raw.orderDate === 'string' || typeof raw.orderDate === 'number') {
//             orderDate = new Date(raw.orderDate as string | number);
//           } else if (
//             typeof raw.orderDate === 'object' &&
//             raw.orderDate !== null &&
//             '_seconds' in raw.orderDate
//           ) {
//             // Firestore Timestamp serialised as { _seconds, _nanoseconds }
//             const ts = raw.orderDate as { _seconds: number; _nanoseconds: number };
//             orderDate = new Date(ts._seconds * 1000);
//           }
//         }
//         return { ...raw, id: orderId, orderDate } as Order;
//       }
//       return null;
//     } catch {
//       return null;
//     }
//   }, [orderId]);

//   // Handle side-effects when we detect a terminal failure status
//   const handleFailureStatus = useCallback((paymentStatus: string) => {
//     if (cartRestoredRef.current) return;
//     if (['cancelled', 'failed', 'chargedback'].includes(paymentStatus)) {
//       const restored = restoreCartFromBackup();
//       if (restored && isMountedRef.current) {
//         cartRestoredRef.current = true;
//         setCartRestored(true);
//       }
//     }
//   }, []);

//   useEffect(() => {
//     isMountedRef.current = true;

//     if (!orderId) {
//       navigate('/');
//       return;
//     }

//     const startPolling = async () => {
//       // ── Initial fetch ──────────────────────────────────────────────────
//       try {
//         const initialOrder = await fetchOrder();

//         if (!isMountedRef.current) return;

//         if (!initialOrder) {
//           setFetchError('Order not found. Please check your email for a confirmation or contact support.');
//           setLoading(false);
//           return;
//         }

//         setOrder(initialOrder);
//         setLoading(false);

//         // Handle failure immediately if order already has terminal bad status
//         handleFailureStatus(initialOrder.paymentStatus);

//         // If already terminal, no need to poll
//         if (TERMINAL_PAYMENT_STATUSES.has(initialOrder.paymentStatus)) {
//           return;
//         }

//         // ── Poll for PayHere webhook update ────────────────────────────
//         // Order is in 'awaiting_payment' — keep checking until it moves
//         // to a terminal paymentStatus (paid / cancelled / failed / chargedback)
//         pollIntervalRef.current = setInterval(async () => {
//           try {
//             const updatedOrder = await fetchOrder();
//             if (!isMountedRef.current) return;
//             if (!updatedOrder) return;

//             setOrder(updatedOrder);
//             handleFailureStatus(updatedOrder.paymentStatus);

//             if (TERMINAL_PAYMENT_STATUSES.has(updatedOrder.paymentStatus)) {
//               stopPolling();
//             }
//           } catch {
//             // Transient network error — keep polling
//           }
//         }, POLL_INTERVAL_MS);

//         // Stop polling after timeout regardless
//         pollTimeoutRef.current = setTimeout(() => {
//           if (!isMountedRef.current) return;
//           stopPolling();
//           setPollingTimedOut(true);
//         }, POLL_TIMEOUT_MS);

//       } catch (err) {
//         if (!isMountedRef.current) return;
//         console.error('Error fetching order:', err);
//         setFetchError('Unable to load your order. Please check your email for confirmation.');
//         setLoading(false);
//       }
//     };

//     startPolling();

//     return () => {
//       isMountedRef.current = false;
//       stopPolling();
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [orderId]);

//   // ─────────────────────────────────────────────────────────────────────────
//   // Derived state
//   // ─────────────────────────────────────────────────────────────────────────

//   const isAwaiting  = order?.paymentStatus === 'awaiting_payment';
//   // Success: PayHere paid OR non-PayHere order (bank_transfer / COD) which
//   // lands with paymentStatus = 'pending'
//   const isSuccess   = order?.paymentStatus === 'paid' || order?.paymentStatus === 'pending';
//   const isFailure   = ['cancelled', 'failed', 'chargedback'].includes(order?.paymentStatus ?? '');

//   // ─────────────────────────────────────────────────────────────────────────
//   // Loading screen
//   // ─────────────────────────────────────────────────────────────────────────

//   if (loading) {
//     return (
//       <div>
//         <Header />
//         <HeaderSpacer />
//         <div
//           className="container"
//           style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
//         >
//           <div className="text-center">
//             <div style={{
//               width: '56px', height: '56px', borderRadius: '50%',
//               border: '5px solid #f0f0f0', borderTop: '5px solid #717fe0',
//               margin: '0 auto 20px', animation: 'spin 0.8s linear infinite',
//             }} />
//             <p className="stext-113 cl6 p-t-20">Loading order details…</p>
//           </div>
//         </div>
//         <Footer />
//         <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
//       </div>
//     );
//   }

//   // ─────────────────────────────────────────────────────────────────────────
//   // Fetch error screen
//   // ─────────────────────────────────────────────────────────────────────────

//   if (fetchError || !order) {
//     return (
//       <div>
//         <Header />
//         <HeaderSpacer />
//         <div
//           className="container"
//           style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
//         >
//           <div className="text-center" style={{ maxWidth: '500px', padding: '0 20px' }}>
//             <i
//               className="zmdi zmdi-alert-circle"
//               style={{ fontSize: '64px', color: '#dc3545', marginBottom: '20px', display: 'block' }}
//             />
//             <h2 className="mtext-111 cl2 p-b-16">Oops!</h2>
//             <p className="stext-113 cl6 p-b-26">
//               {fetchError || 'We could not find your order. Please check your email for a confirmation, or contact support.'}
//             </p>
//             <a
//               href="/"
//               className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
//               style={{ display: 'inline-flex' }}
//             >
//               Back to Shop
//             </a>
//           </div>
//         </div>
//         <Footer />
//       </div>
//     );
//   }

//   // ─────────────────────────────────────────────────────────────────────────
//   // Awaiting payment screen (PayHere webhook not yet received)
//   // ─────────────────────────────────────────────────────────────────────────

//   if (isAwaiting) {
//     return (
//       <div>
//         <Header />
//         <HeaderSpacer />
//         <div
//           className="container"
//           style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
//         >
//           <div className="text-center" style={{ maxWidth: '500px', padding: '0 20px' }}>
//             <div style={{
//               width: '72px', height: '72px', borderRadius: '50%',
//               border: '6px solid #e0e0e0', borderTop: '6px solid #717fe0',
//               margin: '0 auto 24px', animation: 'spin 0.9s linear infinite',
//             }} />
//             <h2 className="mtext-111 cl2 p-b-16">Confirming Your Payment…</h2>
//             <p className="stext-113 cl6 p-b-16">
//               We're waiting for PayHere to confirm your payment.
//               This usually takes just a few seconds — please don't close this page.
//             </p>

//             {pollingTimedOut && (
//               <div style={{
//                 padding: '14px 18px', backgroundColor: '#fff3cd',
//                 border: '1px solid #ffc107', borderRadius: '8px',
//                 marginBottom: '20px', fontSize: '13px', color: '#856404',
//                 textAlign: 'left',
//               }}>
//                 <strong>Taking longer than expected.</strong>
//                 <br />
//                 If you completed your payment, please check your email for a confirmation
//                 or contact our support team quoting order reference:{' '}
//                 <strong>{order.orderNumber}</strong>.
//               </div>
//             )}

//             <p style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
//               Order reference: <strong style={{ color: '#666' }}>{order.orderNumber}</strong>
//             </p>
//           </div>
//         </div>
//         <Footer />
//         <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
//       </div>
//     );
//   }

//   // ─────────────────────────────────────────────────────────────────────────
//   // Failure screen (cancelled / failed / chargedback)
//   // ─────────────────────────────────────────────────────────────────────────

//   if (isFailure) {
//     const isCancelled  = order.paymentStatus === 'cancelled';
//     const isChargedback = order.paymentStatus === 'chargedback';

//     return (
//       <div>
//         <Header />
//         <HeaderSpacer />

//         <div className="container">
//           <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
//             <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
//               Home <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
//             </a>
//             <span className="stext-109 cl4">Order Confirmation</span>
//           </div>
//         </div>

//         <section className="bg0 p-t-75 p-b-120">
//           <div className="container">
//             <div style={{ maxWidth: '600px', margin: '0 auto' }}>

//               {/* Cart restored notice */}
//               {cartRestored && (
//                 <div style={{
//                   padding: '14px 20px', backgroundColor: '#d4edda',
//                   border: '1px solid #c3e6cb', borderRadius: '8px',
//                   display: 'flex', alignItems: 'flex-start', gap: '10px',
//                   marginBottom: '24px',
//                 }}>
//                   <i className="zmdi zmdi-check-circle" style={{ fontSize: '18px', color: '#155724', marginTop: '1px', flexShrink: 0 }} />
//                   <div>
//                     <strong style={{ fontSize: '14px', color: '#155724' }}>Your cart has been restored</strong>
//                     <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#155724' }}>
//                       Your items are still in your cart. You can return to checkout and try again.
//                     </p>
//                   </div>
//                 </div>
//               )}

//               {/* Status card */}
//               <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm text-center">
//                 <i
//                   className={isCancelled ? 'zmdi zmdi-minus-circle' : 'zmdi zmdi-close-circle'}
//                   style={{
//                     fontSize: '80px',
//                     color: isCancelled ? '#ffc107' : '#dc3545',
//                     marginBottom: '20px',
//                     display: 'block',
//                   }}
//                 />

//                 <h2 className="mtext-111 cl2 p-b-16">
//                   {isCancelled
//                     ? 'Payment Cancelled'
//                     : isChargedback
//                       ? 'Payment Chargedback'
//                       : 'Payment Failed'}
//                 </h2>

//                 <p className="stext-113 cl6 p-b-10">
//                   {isCancelled
//                     ? 'You cancelled the payment. Your order has not been placed.'
//                     : isChargedback
//                       ? 'A chargeback was raised on this transaction. Please contact support.'
//                       : 'Unfortunately your payment could not be processed. Your order has not been placed.'}
//                 </p>

//                 {!isCancelled && !isChargedback && (
//                   <p className="stext-113 cl6 p-b-20" style={{ fontSize: '13px', color: '#888' }}>
//                     No charge was made to your account. If you see a pending charge, it will be
//                     automatically reversed within 3–5 business days.
//                   </p>
//                 )}

//                 <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '28px' }}>
//                   Order reference: <strong style={{ color: '#666' }}>{order.orderNumber}</strong>
//                 </p>

//                 {/* Actions */}
//                 <div className="flex-w flex-m" style={{ gap: '10px', justifyContent: 'center' }}>
//                   <a
//                     href="/checkout"
//                     className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer m-b-10"
//                     style={{ flex: '1', maxWidth: '200px' }}
//                   >
//                     <i className="zmdi zmdi-refresh m-r-5" /> Try Again
//                   </a>
//                   <a
//                     href="/"
//                     className="flex-c-m stext-101 cl2 size-116 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer m-b-10"
//                     style={{ flex: '1', maxWidth: '200px' }}
//                   >
//                     Back to Shop
//                   </a>
//                 </div>
//               </div>

//             </div>
//           </div>
//         </section>

//         <Footer />
//         <BackToTop />
//         <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
//       </div>
//     );
//   }

//   // ─────────────────────────────────────────────────────────────────────────
//   // Success screen (paymentStatus = 'paid' OR 'pending' for non-PayHere)
//   // ─────────────────────────────────────────────────────────────────────────

//   const isPaidViaPayhere  = order.paymentStatus === 'paid';
//   const isBankTransfer    = order.paymentMethod === 'bank_transfer';
//   const isCOD             = order.paymentMethod === 'cash_on_delivery';

//   return (
//     <div>
//       <Header />
//       <HeaderSpacer />

//       <div className="container">
//         <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
//           <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
//             Home <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true" />
//           </a>
//           <span className="stext-109 cl4">Order Confirmation</span>
//         </div>
//       </div>

//       <section className="bg0 p-t-75 p-b-120">
//         <div className="container">

//           {/* ── Success banner ── */}
//           <div className="text-center p-b-50">
//             <i
//               className="zmdi zmdi-check-circle"
//               style={{ fontSize: '80px', color: '#28a745', marginBottom: '20px', display: 'block' }}
//             />
//             <h2 className="mtext-111 cl2 p-b-16">
//               {isPaidViaPayhere
//                 ? 'Payment Successful!'
//                 : isBankTransfer
//                   ? 'Order Placed Successfully!'
//                   : isCOD
//                     ? 'Order Placed Successfully!'
//                     : 'Thank You for Your Order!'}
//             </h2>
//             <p className="stext-113 cl6 p-b-10">
//               {isPaidViaPayhere
//                 ? 'Your payment was confirmed and your order is being processed.'
//                 : isBankTransfer
//                   ? 'Your order has been received. We will process it once your payment is verified.'
//                   : isCOD
//                     ? 'Your order has been placed. Please have the payment ready upon delivery.'
//                     : 'Your order has been placed successfully.'}
//             </p>
//             <p className="stext-113 cl6 p-b-5">
//               Order Number: <strong>{order.orderNumber}</strong>
//             </p>
//             <p className="stext-113 cl6 p-t-10">
//               A confirmation email has been sent to <strong>{order.customerEmail}</strong>
//             </p>
//           </div>

//           <div className="row">

//             {/* ── Left: Order Details ── */}
//             <div className="col-lg-7 m-lr-auto m-b-50">
//               <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
//                 <h4 className="mtext-109 cl2 p-b-30">Order Details</h4>

//                 {/* Order Number */}
//                 <div className="flex-w flex-t p-b-13">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Order Number:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6">{order.orderNumber}</span>
//                   </div>
//                 </div>

//                 {/* Order Date */}
//                 <div className="flex-w flex-t p-b-13">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Order Date:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6">
//                       {order.orderDate.toLocaleDateString('en-US', {
//                         year: 'numeric',
//                         month: 'long',
//                         day: 'numeric',
//                         hour: '2-digit',
//                         minute: '2-digit',
//                       })}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Order Status */}
//                 <div className="flex-w flex-t p-b-13">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Order Status:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6" style={statusBadgeStyle()}>
//                       {order.status}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Payment Status */}
//                 <div className="flex-w flex-t p-b-13">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Payment Status:</span>
//                   </div>
//                   <div className="size-211">
//                     <span
//                       className="stext-111 cl6"
//                       style={{
//                         ...paymentStatusBadgeStyle(order.paymentStatus),
//                         padding: '4px 12px',
//                         borderRadius: '4px',
//                         textTransform: 'capitalize',
//                       }}
//                     >
//                       {order.paymentStatus === 'paid'
//                         ? 'Paid'
//                         : order.paymentStatus === 'pending'
//                           ? isBankTransfer
//                             ? 'Awaiting Verification'
//                             : isCOD
//                               ? 'Pay on Delivery'
//                               : 'Pending'
//                           : order.paymentStatus}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Payment Method */}
//                 <div className="flex-w flex-t p-b-30">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Payment Method:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6" style={{ textTransform: 'capitalize' }}>
//                       {order.paymentMethod.replace(/_/g, ' ')}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Bank transfer note */}
//                 {isBankTransfer && (
//                   <div style={{
//                     padding: '14px 16px',
//                     backgroundColor: '#f0f4ff',
//                     borderLeft: '3px solid #717fe0',
//                     borderRadius: '0 6px 6px 0',
//                     marginBottom: '24px',
//                   }}>
//                     <p style={{ margin: 0, fontSize: '13px', color: '#444', lineHeight: '1.6' }}>
//                       <strong>Bank Transfer Instructions:</strong> Please transfer the total amount to
//                       our bank account and ensure your order number <strong>{order.orderNumber}</strong> is
//                       included in the payment reference. Your order will be processed once payment is verified,
//                       typically within 1–2 business days.
//                     </p>
//                   </div>
//                 )}

//                 {/* COD note */}
//                 {isCOD && (
//                   <div style={{
//                     padding: '14px 16px',
//                     backgroundColor: '#f0f4ff',
//                     borderLeft: '3px solid #717fe0',
//                     borderRadius: '0 6px 6px 0',
//                     marginBottom: '24px',
//                   }}>
//                     <p style={{ margin: 0, fontSize: '13px', color: '#444', lineHeight: '1.6' }}>
//                       <strong>Cash on Delivery:</strong> Please have the exact amount of{' '}
//                       <strong>{formatPrice(order.totalAmount)}</strong> ready when your order arrives.
//                     </p>
//                   </div>
//                 )}

//                 {/* Shipping Address */}
//                 <h5 className="mtext-110 cl2 p-b-20 p-t-20">Shipping Address</h5>

//                 <div className="flex-w flex-t p-b-10">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Name:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6">{order.shippingAddress.fullName}</span>
//                   </div>
//                 </div>

//                 <div className="flex-w flex-t p-b-10">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Address:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6">
//                       {order.shippingAddress.addressLine1}
//                       <br />
//                       {order.shippingAddress.addressLine2 && (
//                         <>{order.shippingAddress.addressLine2}<br /></>
//                       )}
//                       {order.shippingAddress.city}
//                       {order.shippingAddress.state ? `, ${order.shippingAddress.state}` : ''}
//                       {' '}{order.shippingAddress.postalCode}
//                       <br />
//                       {order.shippingAddress.country}
//                     </span>
//                   </div>
//                 </div>

//                 <div className="flex-w flex-t">
//                   <div className="size-210">
//                     <span className="stext-110 cl2">Phone:</span>
//                   </div>
//                   <div className="size-211">
//                     <span className="stext-111 cl6">{order.shippingAddress.phoneNumber}</span>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* ── Right: Order Summary ── */}
//             <div className="col-lg-5 m-lr-auto m-b-50">
//               <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
//                 <h4 className="mtext-109 cl2 p-b-30">Order Summary</h4>

//                 {/* Items */}
//                 {order.items.map((item, index) => (
//                   <div key={index} className="flex-w flex-t bor12 p-b-13 p-t-15">
//                     <div className="size-208 w-full-ssm">
//                       <span className="stext-111 cl2">
//                         {item.productName}
//                         {item.variantSelections && item.variantSelections.length > 0 && (
//                           <span className="stext-111 cl6">
//                             {' '}({getVariantText(item.variantSelections)})
//                           </span>
//                         )}
//                         <span className="stext-111 cl6"> × {item.quantity}</span>
//                       </span>
//                       {/* Show discount if applicable */}
//                       {item.discountPercentage > 0 && (
//                         <div style={{ marginTop: '4px' }}>
//                           <span style={{
//                             fontSize: '11px', color: '#aaa',
//                             textDecoration: 'line-through', marginRight: '6px',
//                           }}>
//                             {formatPrice(item.unitPrice)}
//                           </span>
//                           <span style={{ fontSize: '11px', color: '#e74c3c', fontWeight: 600 }}>
//                             -{item.discountPercentage}%
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                     <div className="size-209 p-t-1">
//                       <span className="mtext-110 cl2">{formatPrice(item.totalPrice)}</span>
//                     </div>
//                   </div>
//                 ))}

//                 {/* Subtotal */}
//                 <div className="flex-w flex-t bor12 p-t-15 p-b-13">
//                   <div className="size-208">
//                     <span className="stext-110 cl2">Subtotal</span>
//                   </div>
//                   <div className="size-209 p-t-1">
//                     <span className="mtext-110 cl2">{formatPrice(order.subtotal)}</span>
//                   </div>
//                 </div>

//                 {/* Discount */}
//                 {order.discountAmount != null && order.discountAmount > 0 && (
//                   <div className="flex-w flex-t bor12 p-t-15 p-b-13">
//                     <div className="size-208">
//                       <span className="stext-110 cl2" style={{ color: '#e74c3c' }}>You Saved</span>
//                     </div>
//                     <div className="size-209 p-t-1">
//                       <span className="mtext-110 cl2" style={{ color: '#e74c3c' }}>
//                         -{formatPrice(order.discountAmount)}
//                       </span>
//                     </div>
//                   </div>
//                 )}

//                 {/* Tax */}
//                 <div className="flex-w flex-t bor12 p-t-15 p-b-13">
//                   <div className="size-208">
//                     <span className="stext-110 cl2">Tax</span>
//                   </div>
//                   <div className="size-209 p-t-1">
//                     <span className="mtext-110 cl2">{formatPrice(order.taxAmount)}</span>
//                   </div>
//                 </div>

//                 {/* Shipping */}
//                 <div className="flex-w flex-t bor12 p-t-15 p-b-13">
//                   <div className="size-208">
//                     <span className="stext-110 cl2">Shipping</span>
//                   </div>
//                   <div className="size-209 p-t-1">
//                     <span className="mtext-110 cl2">
//                       {order.shippingCost === 0 ? 'Free' : formatPrice(order.shippingCost)}
//                     </span>
//                   </div>
//                 </div>

//                 {/* Total */}
//                 <div className="flex-w flex-t p-t-27 p-b-33">
//                   <div className="size-208">
//                     <span className="mtext-101 cl2">Total</span>
//                   </div>
//                   <div className="size-209 p-t-1">
//                     <span className="mtext-110 cl2">{formatPrice(order.totalAmount)}</span>
//                   </div>
//                 </div>

//                 {/* Actions */}
//                 <div className="flex-w flex-m" style={{ gap: '10px' }}>
//                   <a
//                     href="/"
//                     className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer m-b-10"
//                     style={{ flex: 1 }}
//                   >
//                     Continue Shopping
//                   </a>
//                 </div>
//               </div>
//             </div>

//           </div>{/* end .row */}

//           {/* ── What Happens Next ── */}
//           <div className="row p-t-30">
//             <div className="col-12">
//               <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm text-center">
//                 <h5 className="mtext-110 cl2 p-b-20">What Happens Next?</h5>
//                 <div className="row">
//                   <div className="col-md-4 p-b-20">
//                     <i
//                       className="zmdi zmdi-email"
//                       style={{ fontSize: '40px', color: '#717fe0', marginBottom: '15px', display: 'block' }}
//                     />
//                     <p className="stext-113 cl2 p-b-5"><strong>Confirmation Email</strong></p>
//                     <p className="stext-113 cl6">You'll receive an order confirmation email shortly.</p>
//                   </div>
//                   <div className="col-md-4 p-b-20">
//                     <i
//                       className="zmdi zmdi-settings"
//                       style={{ fontSize: '40px', color: '#717fe0', marginBottom: '15px', display: 'block' }}
//                     />
//                     <p className="stext-113 cl2 p-b-5"><strong>Order Processing</strong></p>
//                     <p className="stext-113 cl6">
//                       {isBankTransfer
//                         ? "We'll verify your payment and process your order within 1–2 business days."
//                         : "We'll process your order and prepare it for shipment."}
//                     </p>
//                   </div>
//                   <div className="col-md-4 p-b-20">
//                     <i
//                       className="zmdi zmdi-truck"
//                       style={{ fontSize: '40px', color: '#717fe0', marginBottom: '15px', display: 'block' }}
//                     />
//                     <p className="stext-113 cl2 p-b-5"><strong>Shipping Updates</strong></p>
//                     <p className="stext-113 cl6">
//                       You'll receive tracking information once your order ships.{' '}
//                       <a href={`/order-tracking?orderId=${order.id}`} className="cl1 hov-cl2 trans-04">
//                         Track here
//                       </a>.
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>

//         </div>
//       </section>

//       <Footer />
//       <BackToTop />

//       <style>{`
//         .size-210 { width: 40%; }
//         .size-211 { width: 60%; }

//         @media (max-width: 576px) {
//           .size-210,
//           .size-211 { width: 100%; }
//         }

//         @keyframes spin { to { transform: rotate(360deg); } }

//         @media print {
//           header, footer, .bread-crumb,
//           a.bg3, a.bg8, button { display: none !important; }
//         }
//       `}</style>
//     </div>
//   );
// };

// export default OrderConfirmation;

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { useCompany } from '../../Contexts/CompanyContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Firebase/Config';
import HeaderSpacer from '../Header/HeaderSpacer';

interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: Array<{
    productName: string;
    variantSelections?: Array<{ variantType: string; variantValue: string }>;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  subtotal: number;
  taxAmount: number;
  shippingCost: number;
  totalAmount: number;
  status: string;
  paymentStatus: string;
  paymentMethod: string;
  shippingAddress: {
    fullName: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phoneNumber: string;
  };
  orderDate: Date;
}

const OrderConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  // useCompany provides formatPrice which reads currency from company profile
  const { formatPrice } = useCompany();

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        navigate('/');
        return;
      }

      try {
        const orderRef = doc(db, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);

        if (orderSnap.exists()) {
          const data = orderSnap.data();
          setOrder({
            id: orderSnap.id,
            ...data,
            orderDate: data.orderDate?.toDate() || new Date(),
          } as Order);
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, navigate]);

  const getVariantText = (
    variantSelections?: Array<{ variantType: string; variantValue: string }>
  ) => {
    if (!variantSelections || variantSelections.length === 0) return '';
    return variantSelections.map((s) => `${s.variantType}: ${s.variantValue}`).join(', ');
  };

  if (loading) {
    return (
      <div>
        <Header />
      <HeaderSpacer />
        <div
          className="container"
          style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="stext-113 cl6 p-t-20">Loading order details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div>
      <Header />
      <HeaderSpacer />

      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Order Confirmation</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Success Message ── */}
          <div className="text-center p-b-50">
            <i
              className="zmdi zmdi-check-circle"
              style={{ fontSize: '80px', color: '#28a745', marginBottom: '20px', display: 'block' }}
            ></i>
            <h2 className="mtext-111 cl2 p-b-16">Thank You for Your Order!</h2>
            <p className="stext-113 cl6 p-b-10">Your order has been placed successfully.</p>
            <p className="stext-113 cl6">
              Order Number: <strong>{order.orderNumber}</strong>
            </p>
            <p className="stext-113 cl6 p-t-10">
              A confirmation email has been sent to <strong>{order.customerEmail}</strong>
            </p>

            {/* Track Order shortcut */}
            {/* <div className="p-t-25">
              <a
                href={`/order-tracking?orderId=${order.id}`}
                className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
                style={{ display: 'inline-flex' }}
              >
                <i className="zmdi zmdi-truck m-r-8"></i>
                Track My Order
              </a>
            </div> */}
          </div>

          <div className="row">
            {/* ── Order Details ── */}
            <div className="col-lg-7 m-lr-auto m-b-50">
              <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
                <h4 className="mtext-109 cl2 p-b-30">Order Details</h4>

                <div className="flex-w flex-t p-b-13">
                  <div className="size-210">
                    <span className="stext-110 cl2">Order Number:</span>
                  </div>
                  <div className="size-211">
                    <span className="stext-111 cl6">{order.orderNumber}</span>
                  </div>
                </div>

                <div className="flex-w flex-t p-b-13">
                  <div className="size-210">
                    <span className="stext-110 cl2">Order Date:</span>
                  </div>
                  <div className="size-211">
                    <span className="stext-111 cl6">
                      {order.orderDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>

                <div className="flex-w flex-t p-b-13">
                  <div className="size-210">
                    <span className="stext-110 cl2">Order Status:</span>
                  </div>
                  <div className="size-211">
                    <span
                      className="stext-111 cl6"
                      style={{
                        textTransform: 'capitalize',
                        backgroundColor: '#ffc107',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        color: '#000',
                      }}
                    >
                      {order.status}
                    </span>
                  </div>
                </div>

                <div className="flex-w flex-t p-b-13">
                  <div className="size-210">
                    <span className="stext-110 cl2">Payment Status:</span>
                  </div>
                  <div className="size-211">
                    <span
                      className="stext-111 cl6"
                      style={{
                        textTransform: 'capitalize',
                        backgroundColor: '#ffc107',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        color: '#000',
                      }}
                    >
                      {order.paymentStatus}
                    </span>
                  </div>
                </div>

                <div className="flex-w flex-t p-b-30">
                  <div className="size-210">
                    <span className="stext-110 cl2">Payment Method:</span>
                  </div>
                  <div className="size-211">
                    <span className="stext-111 cl6" style={{ textTransform: 'capitalize' }}>
                      {order.paymentMethod.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <h5 className="mtext-110 cl2 p-b-20 p-t-20">Shipping Address</h5>

                <div className="flex-w flex-t p-b-10">
                  <div className="size-210">
                    <span className="stext-110 cl2">Name:</span>
                  </div>
                  <div className="size-211">
                    <span className="stext-111 cl6">{order.shippingAddress.fullName}</span>
                  </div>
                </div>

                <div className="flex-w flex-t p-b-10">
                  <div className="size-210">
                    <span className="stext-110 cl2">Address:</span>
                  </div>
                  <div className="size-211">
                    <span className="stext-111 cl6">
                      {order.shippingAddress.addressLine1}
                      <br />
                      {order.shippingAddress.addressLine2 && (
                        <>{order.shippingAddress.addressLine2}<br /></>
                      )}
                      {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
                      {order.shippingAddress.postalCode}
                      <br />
                      {order.shippingAddress.country}
                    </span>
                  </div>
                </div>

                <div className="flex-w flex-t">
                  <div className="size-210">
                    <span className="stext-110 cl2">Phone:</span>
                  </div>
                  <div className="size-211">
                    <span className="stext-111 cl6">{order.shippingAddress.phoneNumber}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Order Summary ── */}
            <div className="col-lg-5 m-lr-auto m-b-50">
              <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm">
                <h4 className="mtext-109 cl2 p-b-30">Order Summary</h4>

                {/* Items */}
                {order.items.map((item, index) => (
                  <div key={index} className="flex-w flex-t bor12 p-b-13 p-t-15">
                    <div className="size-208 w-full-ssm">
                      <span className="stext-111 cl2">
                        {item.productName}
                        {item.variantSelections && item.variantSelections.length > 0 && (
                          <span className="stext-111 cl6">
                            {' '}({getVariantText(item.variantSelections)})
                          </span>
                        )}
                        <span className="stext-111 cl6"> × {item.quantity}</span>
                      </span>
                    </div>
                    <div className="size-209 p-t-1">
                      <span className="mtext-110 cl2">{formatPrice(item.totalPrice)}</span>
                    </div>
                  </div>
                ))}

                {/* Subtotal */}
                <div className="flex-w flex-t bor12 p-t-15 p-b-13">
                  <div className="size-208">
                    <span className="stext-110 cl2">Subtotal</span>
                  </div>
                  <div className="size-209 p-t-1">
                    <span className="mtext-110 cl2">{formatPrice(order.subtotal)}</span>
                  </div>
                </div>

                {/* Tax */}
                <div className="flex-w flex-t bor12 p-t-15 p-b-13">
                  <div className="size-208">
                    <span className="stext-110 cl2">Tax</span>
                  </div>
                  <div className="size-209 p-t-1">
                    <span className="mtext-110 cl2">{formatPrice(order.taxAmount)}</span>
                  </div>
                </div>

                {/* Shipping */}
                <div className="flex-w flex-t bor12 p-t-15 p-b-13">
                  <div className="size-208">
                    <span className="stext-110 cl2">Shipping</span>
                  </div>
                  <div className="size-209 p-t-1">
                    <span className="mtext-110 cl2">
                      {order.shippingCost === 0 ? 'Free' : formatPrice(order.shippingCost)}
                    </span>
                  </div>
                </div>

                {/* Total */}
                <div className="flex-w flex-t p-t-27 p-b-33">
                  <div className="size-208">
                    <span className="mtext-101 cl2">Total</span>
                  </div>
                  <div className="size-209 p-t-1">
                    <span className="mtext-110 cl2">{formatPrice(order.totalAmount)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex-w flex-m" style={{ gap: 10 }}>
                  <a
                    href="/"
                    className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer m-b-10"
                    style={{ flex: 1 }}
                  >
                    Continue Shopping
                  </a>
                  {/* <a
                    href={`/order-tracking?orderId=${order.id}`}
                    className="flex-c-m stext-101 cl2 size-116 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer m-b-10"
                    style={{ flex: 1 }}
                  >
                    <i className="zmdi zmdi-truck m-r-5"></i>
                    Track Order
                  </a> */}
                  {/* <button
                    onClick={() => window.print()}
                    className="flex-c-m stext-101 cl2 size-116 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer m-b-10"
                    style={{ flex: 1 }}
                  >
                    Print Order
                  </button> */}
                </div>
              </div>
            </div>
          </div>

          {/* ── What Happens Next ── */}
          <div className="row p-t-30">
            <div className="col-12">
              <div className="bor10 p-lr-40 p-t-30 p-b-40 m-lr-15-sm text-center">
                <h5 className="mtext-110 cl2 p-b-20">What Happens Next?</h5>
                <div className="row">
                  <div className="col-md-4 p-b-20">
                    <i
                      className="zmdi zmdi-email"
                      style={{ fontSize: '40px', color: '#717fe0', marginBottom: '15px', display: 'block' }}
                    ></i>
                    <p className="stext-113 cl2 p-b-5"><strong>Confirmation Email</strong></p>
                    <p className="stext-113 cl6">You'll receive an order confirmation email shortly.</p>
                  </div>
                  <div className="col-md-4 p-b-20">
                    <i
                      className="zmdi zmdi-settings"
                      style={{ fontSize: '40px', color: '#717fe0', marginBottom: '15px', display: 'block' }}
                    ></i>
                    <p className="stext-113 cl2 p-b-5"><strong>Order Processing</strong></p>
                    <p className="stext-113 cl6">We'll process your order and prepare it for shipment.</p>
                  </div>
                  <div className="col-md-4 p-b-20">
                    <i
                      className="zmdi zmdi-truck"
                      style={{ fontSize: '40px', color: '#717fe0', marginBottom: '15px', display: 'block' }}
                    ></i>
                    <p className="stext-113 cl2 p-b-5"><strong>Shipping Updates</strong></p>
                    <p className="stext-113 cl6">
                      You'll receive tracking information once your order ships.{' '}
                      <a href={`/order-tracking?orderId=${order.id}`} className="cl1 hov-cl2 trans-04">
                        Track here
                      </a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        .size-210 { width: 40%; }
        .size-211 { width: 60%; }

        @media (max-width: 576px) {
          .size-210,
          .size-211 { width: 100%; }
        }

        @media print {
          header, footer, .bread-crumb,
          a.bg3, a.bg8, button { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default OrderConfirmation;